import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layouts/dashboard/DashboardLayout';
import DataTable, { TagCell, AmountCell, DateCell } from '../../components/dashboard/tables/DataTable';
import { EmptyState } from '../../components/dashboard/common/StyledComponents';
import axios from 'axios';
import { useOrganization } from '../../contexts/OrganizationContext';

const claimsColumns = [
  { key: 'date', label: 'Date', width: '15%' },
  { key: 'id', label: 'Claim ID', width: '34%' },
  { key: 'amount', label: 'Amount', width: '13%' },
  { key: 'status', label: 'Status', width: '13%' },
  { key: 'type', label: 'Type', width: '25%' }
];

const getStatusTagProps = (status) => ({
  background: status === 'Approved' ? '#E8F5E9' : 
              status === 'Pending' ? '#FFF3E0' : '#FFEBEE',
  color: status === 'Approved' ? '#2E7D32' : 
         status === 'Pending' ? '#E65100' : '#C62828'
});

const renderClaimCell = (key, value, row) => {
  switch (key) {
    case 'date':
      return <DateCell value={value} />;
    case 'amount':
      return <AmountCell value={value} />;
    case 'status':
      return <TagCell value={value} getTagProps={getStatusTagProps} />;
    case 'type':
      return <TagCell value={value} />;
    default:
      return value || '-';
  }
};

const ClaimsHistory = () => {
  const [ppClaims, setPPClaims] = useState([]);
  const [previousClaims, setPreviousClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const { organizationId, loading: contextLoading } = useOrganization();

  useEffect(() => {
    const fetchClaims = async () => {
      console.log('ClaimsHistory: Current organizationId from context:', organizationId);
      
      if (!organizationId) {
        console.warn('ClaimsHistory: No organizationId available from context');
        return;
      }
      
      try {
        setLoading(true);
        console.log('ClaimsHistory: Fetching claims for organization:', organizationId);
        
        const response = await axios.get(`/api/dashboard/claims/${organizationId}`, {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
            'Expires': '0',
          }
        });

        // Parse the Pipedrive custom field JSON
        const ppClaimsData = response.data?.data?.pipedriveData?.['5f03d3a372383b92416634fe4581f88d024f8ca0'];
        console.log('Raw PP Claims Data:', ppClaimsData);
        
        if (ppClaimsData) {
          try {
            const parsedData = typeof ppClaimsData === 'string' ? JSON.parse(ppClaimsData) : ppClaimsData;
            console.log('Parsed PP Claims Data:', parsedData);
            
            if (parsedData?.claims && Array.isArray(parsedData.claims)) {
              setPPClaims(parsedData.claims.map(claim => ({
                ...claim,
                id: claim.id || `PP-${new Date(claim.date).getTime()}`,
                status: claim.status || 'Pending',
                type: claim.type || 'Planet Protection'
              })));
            }
          } catch (e) {
            console.error('Error parsing PP claims data:', e);
            setError('Error parsing Planet Protection claims data');
          }
        }

        // Set previous claims from the response
        if (response.data?.data?.previousClaims) {
          setPreviousClaims(response.data.data.previousClaims);
        }

        setError(null);
      } catch (err) {
        setError(err.message || 'Failed to fetch claims data');
        console.error('Error fetching claims data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchClaims();
  }, [organizationId]);

  if (contextLoading || loading) {
    return (
      <DashboardLayout title="Claims History" currentPath="/dashboard/claims">
        <EmptyState>Loading claims data...</EmptyState>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout title="Claims History" currentPath="/dashboard/claims">
        <EmptyState>Error loading claims: {error}</EmptyState>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Claims History" currentPath="/dashboard/claims">
      <DataTable 
        title="Previous Claims"
        columns={claimsColumns}
        data={previousClaims}
        renderCell={renderClaimCell}
        emptyState="No previous claims available"
      />
      <DataTable 
        title="Planet Protection Claims"
        columns={claimsColumns}
        data={ppClaims}
        renderCell={renderClaimCell}
        emptyState="No Planet Protection claims available"
      />
    </DashboardLayout>
  );
};

export default ClaimsHistory; 